/**
 * AdminCreateVideoPage.jsx — Phase-1 one-click video creation wizard.
 *
 * Route: /admin/video-studio/create
 *
 * Flow (4 steps)
 * ──────────────
 *   1. Paste URL                → POST /enrich  (AI + scrape)
 *   2. Review extracted details → editable form
 *   3. Settings                 → duration · aspect · voice · bg · model
 *   4. Render                   → POST /render → poll for the MP4 URL
 *
 * Why a wizard and not a single form?
 *   • The enrichment step is slow (5-15s — Claude call + image fetch).
 *     Splitting lets us show a clean spinner instead of a frozen page.
 *   • Step 3 settings are *purely* render-config — we keep them
 *     separate so the operator can re-render with different settings
 *     without re-running the AI.
 *   • Step 4 polls the catalogue for the new MP4 URL so we don't have
 *     to wait synchronously for a 60s ffmpeg job.
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE } from "../../lib/api";
import { toast } from "sonner";

const STEPS = [
  { n: 1, key: "url",      label: "Paste URL" },
  { n: 2, key: "details",  label: "Review details" },
  { n: 3, key: "settings", label: "Settings" },
  { n: 4, key: "render",   label: "Render" },
];

export default function AdminCreateVideoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step-1 state
  const [url, setUrl] = useState("");
  const [enriching, setEnriching] = useState(false);

  // Step-2 state (populated from /enrich)
  const [enriched, setEnriched] = useState(null);

  // Step-3 state
  const [duration, setDuration]       = useState(30);
  const [orientation, setOrientation] = useState("vertical");
  const [voice, setVoice]             = useState("nova");
  const [bgMode, setBgMode]           = useState("auto");
  const [voicesCatalogue, setVoicesCatalogue] = useState([]);
  const [ratiosCatalogue, setRatiosCatalogue] = useState([]);
  const [durations, setDurations]     = useState([15, 30, 45, 60, 90]);
  const [presenters, setPresenters]   = useState([]);
  const [presenterAvatarId, setPresenterAvatarId] = useState("");
  const [heygenQuota, setHeygenQuota] = useState(null);

  // Step-4 state
  const [rendering, setRendering] = useState(false);
  const [renderJob, setRenderJob] = useState(null);
  const [videoReady, setVideoReady] = useState(null);

  // Load voices + aspect ratios when entering step 3
  useEffect(() => {
    if (step !== 3) return;
    fetch(`${API_BASE}/video-studio/wizard/voices`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setVoicesCatalogue(d?.voices || []))
      .catch(() => {});
    fetch(`${API_BASE}/video-studio/wizard/aspect-ratios`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        setRatiosCatalogue(d?.ratios || []);
        setDurations(d?.durations || [15, 30, 45, 60, 90]);
      })
      .catch(() => {});
    fetch(`${API_BASE}/video-studio/wizard/presenters/avatars`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setPresenters(d?.avatars || []))
      .catch(() => {});
    fetch(`${API_BASE}/video-studio/wizard/presenters/quota`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setHeygenQuota(d))
      .catch(() => {});
  }, [step]);

  const handleEnrich = async () => {
    if (!url.trim()) {
      toast.error("Paste an Amazon URL first");
      return;
    }
    setEnriching(true);
    try {
      const r = await fetch(`${API_BASE}/video-studio/wizard/enrich`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amazon_url: url.trim() }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j?.detail || "Enrichment failed");
        return;
      }
      setEnriched(j);
      setStep(2);
    } catch (e) {
      toast.error("Network error during enrichment");
    } finally {
      setEnriching(false);
    }
  };

  const handleRender = async () => {
    setRendering(true);
    try {
      const body = {
        amazon_url: url.trim(),
        title: enriched.title,
        image_url: enriched.image_url,
        category: enriched.category,
        voiceover_script: enriched.voiceover_script,
        price_amount: enriched.price_amount,
        orientation, duration, voice, bg_mode: bgMode,
        presenter_avatar_id: presenterAvatarId || null,
      };
      const r = await fetch(`${API_BASE}/video-studio/wizard/render`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j?.detail || "Render failed");
        return;
      }
      setRenderJob(j);
      setStep(4);
      toast.success("Video queued! Polling for completion…");
    } catch (e) {
      toast.error("Network error queuing render");
    } finally {
      setRendering(false);
    }
  };

  // Poll for the rendered video on step 4
  const checkVideoReady = useCallback(async () => {
    if (!renderJob?.estimated_url) return;
    try {
      const r = await fetch(renderJob.estimated_url, { method: "HEAD" });
      if (r.ok) setVideoReady(renderJob.estimated_url);
    } catch (e) {/* still rendering */}
  }, [renderJob]);

  useEffect(() => {
    if (step !== 4 || videoReady) return;
    const id = setInterval(checkVideoReady, 5000);
    checkVideoReady();
    return () => clearInterval(id);
  }, [step, videoReady, checkVideoReady]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky header with progress */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/admin" className="text-xs text-slate-500 hover:text-slate-900"
                data-testid="link-back-admin">← Admin</Link>
          <h1 className="text-base font-bold text-slate-900">Create Video</h1>
          <ol className="ml-auto flex items-center gap-3 text-xs">
            {STEPS.map(s => (
              <li key={s.n}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
                    step === s.n
                      ? "bg-amber-400 text-slate-900 font-semibold"
                      : step > s.n
                        ? "text-emerald-600"
                        : "text-slate-400"
                  }`}
                  data-testid={`step-${s.key}`}>
                <span className="w-5 h-5 rounded-full bg-white border grid place-items-center text-[10px] font-bold">
                  {step > s.n ? "✓" : s.n}
                </span>
                {s.label}
              </li>
            ))}
          </ol>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* ─── Step 1: paste URL ─── */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
               data-testid="wizard-step-url">
            <h2 className="text-xl font-bold text-slate-900 mb-1">
              Paste a product URL
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              We&apos;ll extract the ASIN, fetch the product image, and use Claude
              to draft a polished script in ~10 seconds.
            </p>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://www.amazon.com.au/dp/B0XXXXXXXX/..."
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm font-mono"
              data-testid="input-product-url"
            />
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleEnrich}
                disabled={enriching}
                className="px-8 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-700 disabled:opacity-50"
                data-testid="btn-enrich">
                {enriching ? "AI is analysing…" : "Continue →"}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Tip: products already in HustleShop are loaded from cache — much
              faster than re-scraping Amazon.
            </p>
          </div>
        )}

        {/* ─── Step 2: review extracted details ─── */}
        {step === 2 && enriched && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
               data-testid="wizard-step-details">
            <h2 className="text-xl font-bold text-slate-900 mb-1">
              Review the details
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Edit anything that needs tightening before we render.
              {enriched.from_cache && (
                <span className="ml-2 text-emerald-600">· loaded from cache</span>
              )}
            </p>

            <div className="grid md:grid-cols-[200px_1fr] gap-6">
              <div>
                <img src={enriched.image_url} alt="" className="w-full aspect-square object-contain bg-white border border-slate-200 rounded-lg" />
                <div className="text-[11px] text-slate-500 font-mono mt-2">
                  {enriched.asin} · {enriched.amazon_domain}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  {enriched.price_amount ? `$${enriched.price_amount.toFixed(2)} AUD` : "no price"}
                </div>
              </div>
              <div className="space-y-3">
                <Field label="Title" testid="input-title"
                       value={enriched.title}
                       onChange={v => setEnriched({ ...enriched, title: v })} />
                <SelectField label="Category" testid="select-category"
                       value={enriched.category}
                       onChange={v => setEnriched({ ...enriched, category: v })}
                       options={["fishing","camping","marine","home-garden","kitchen","electronics","sports-fitness","tools-diy","lifestyle"]} />
                <TextareaField label="Voiceover script (60-90 words)"
                       testid="textarea-script"
                       rows={5}
                       value={enriched.voiceover_script || ""}
                       onChange={v => setEnriched({ ...enriched, voiceover_script: v })} />

                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 font-medium">
                    AI-extracted features ({enriched.features?.length || 0})
                  </summary>
                  <ul className="list-disc pl-5 mt-2 text-slate-600 space-y-1">
                    {(enriched.features || []).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </details>
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 font-medium">
                    Customer benefits ({enriched.benefits?.length || 0})
                  </summary>
                  <ul className="list-disc pl-5 mt-2 text-slate-600 space-y-1">
                    {(enriched.benefits || []).map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </details>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(1)}
                      className="text-sm text-slate-600 hover:text-slate-900"
                      data-testid="btn-back-step1">← Back</button>
              <button onClick={() => setStep(3)}
                      className="px-8 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-700"
                      data-testid="btn-to-settings">
                Settings →
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 3: render settings ─── */}
        {step === 3 && enriched && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
               data-testid="wizard-step-settings">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Settings</h2>
            <p className="text-sm text-slate-500 mb-6">
              Pick the format, voice, and background. Defaults work for most.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <Group label="Aspect ratio">
                  {ratiosCatalogue.map(r => (
                    <ChipOption key={r.id} active={orientation === r.id}
                                onClick={() => setOrientation(r.id)}
                                testid={`opt-aspect-${r.id}`}>
                      <div className="font-bold text-sm">{r.label}</div>
                      <div className="text-[11px] opacity-70">{r.use}</div>
                    </ChipOption>
                  ))}
                </Group>

                <Group label="Duration">
                  <div className="flex flex-wrap gap-2">
                    {durations.map(d => (
                      <button key={d} type="button"
                              onClick={() => setDuration(d)}
                              data-testid={`opt-duration-${d}`}
                              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
                                duration === d
                                  ? "bg-amber-400 text-slate-900"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {d}s
                      </button>
                    ))}
                  </div>
                </Group>

                <Group label="Background source">
                  {[
                    { id: "auto",  label: "Auto (tier-based)", desc: "Premium products → live video bg. Budget → still bg with pan." },
                    { id: "image", label: "Still image + pan", desc: "Faster render, lighter file." },
                    { id: "video", label: "Live video clip",   desc: "Real motion, larger files (~5MB)." },
                  ].map(o => (
                    <ChipOption key={o.id} active={bgMode === o.id}
                                onClick={() => setBgMode(o.id)}
                                testid={`opt-bg-${o.id}`}>
                      <div className="font-bold text-sm">{o.label}</div>
                      <div className="text-[11px] opacity-70">{o.desc}</div>
                    </ChipOption>
                  ))}
                </Group>
              </div>

              <div>
                <Group label="Voice">
                  {voicesCatalogue.map(v => (
                    <VoiceCard key={v.id} v={v} active={voice === v.id}
                                onPick={() => setVoice(v.id)} />
                  ))}
                </Group>

                <Group label="AI Presenter (optional — HeyGen)">
                  {heygenQuota && (
                    <div className={`mb-2 text-[11px] px-2.5 py-1.5 rounded-md border ${
                        heygenQuota.credits_remaining > 200
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : heygenQuota.credits_remaining > 0
                            ? "border-amber-200 bg-amber-50 text-amber-800"
                            : "border-rose-200 bg-rose-50 text-rose-700"
                      }`}
                      data-testid="heygen-quota">
                      HeyGen credits: <strong>{heygenQuota.credits_remaining.toLocaleString()}</strong>
                      {" "}·{" "}~{heygenQuota.approx_minutes} min of avatar video
                      {heygenQuota.credits_remaining === 0 && (
                        <> · <a href="https://app.heygen.com/billing/api"
                                  target="_blank" rel="noreferrer"
                                  className="underline">top up</a></>
                      )}
                    </div>
                  )}
                  <PresenterPicker
                    presenters={presenters}
                    selected={presenterAvatarId}
                    onSelect={setPresenterAvatarId}
                  />
                </Group>
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(2)}
                      className="text-sm text-slate-600 hover:text-slate-900"
                      data-testid="btn-back-step2">← Back</button>
              <button onClick={handleRender} disabled={rendering}
                      className="px-8 py-3 bg-amber-400 text-slate-900 text-sm font-bold rounded-lg hover:bg-amber-300 disabled:opacity-50"
                      data-testid="btn-render">
                {rendering ? "Queuing…" : "Render video"}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 4: render in flight ─── */}
        {step === 4 && renderJob && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
               data-testid="wizard-step-render">
            {videoReady ? (
              <>
                <div className="text-emerald-600 text-4xl mb-3">✓</div>
                <h2 className="text-xl font-bold text-slate-900">Video ready!</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Saved to <span className="font-mono">cdn.hustleplaza.au</span>.
                </p>
                <video src={videoReady} controls
                       className="mx-auto mt-6 max-h-[60vh] rounded-lg shadow-lg"
                       data-testid="video-player" />
                <div className="mt-6 flex gap-3 justify-center">
                  <a href={videoReady} download
                     className="px-5 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-700"
                     data-testid="btn-download">⬇ Download MP4</a>
                  <button onClick={() => { setStep(1); setUrl(""); setEnriched(null); setRenderJob(null); setVideoReady(null); }}
                          className="px-5 py-2.5 bg-amber-400 text-slate-900 text-sm font-semibold rounded-lg hover:bg-amber-300"
                          data-testid="btn-create-another">
                    + Create another
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="inline-block animate-spin w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full mb-4" />
                <h2 className="text-xl font-bold text-slate-900">Rendering…</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Usually ~1 minute. We&apos;ll auto-refresh when it&apos;s ready.
                </p>
                <code className="block mt-4 text-[11px] text-slate-400 break-all">
                  {renderJob.estimated_url}
                </code>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ─── small UI atoms ─────────────────────────────────────────────────
function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide mb-1">{label}</div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
             data-testid={testid}
             className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide mb-1">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function TextareaField({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide mb-1">{label}</div>
      <textarea value={value} rows={rows}
                onChange={e => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono leading-relaxed" />
    </label>
  );
}
function Group({ label, children }) {
  return (
    <div className="mb-6">
      <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">{label}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function ChipOption({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition ${
              active
                ? "border-amber-400 bg-amber-50"
                : "border-slate-200 bg-white hover:border-slate-300"}`}>
      {children}
    </button>
  );
}
function VoiceCard({ v, active, onPick }) {
  const [previewing, setPreviewing] = useState(false);
  const play = async () => {
    setPreviewing(true);
    try {
      await fetch(`${API_BASE}/video-studio/wizard/voices/${v.id}/preview`, {
        method: "POST", credentials: "include",
      });
      const audio = new Audio(v.sample_url + `?_=${Date.now()}`);
      audio.play();
      audio.onended = () => setPreviewing(false);
    } catch {
      setPreviewing(false);
    }
  };
  return (
    <div className={`px-3 py-2.5 rounded-lg border-2 transition ${
        active ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}
         data-testid={`voice-card-${v.id}`}>
      <div className="flex items-center justify-between">
        <button onClick={onPick} className="text-left flex-1"
                data-testid={`btn-pick-voice-${v.id}`}>
          <div className="font-bold text-sm">{v.label}</div>
          <div className="text-[11px] text-slate-500">{v.blurb}</div>
        </button>
        <button onClick={play} disabled={previewing}
                className="ml-2 px-2.5 py-1 text-[11px] bg-slate-900 text-white rounded-md hover:bg-slate-700 disabled:opacity-50"
                data-testid={`btn-preview-voice-${v.id}`}>
          {previewing ? "♪" : "▶ preview"}
        </button>
      </div>
    </div>
  );
}

function PresenterPicker({ presenters, selected, onSelect }) {
  // Keep the catalogue tight — top 8 most useful avatars (landscape Women first)
  const sorted = [...presenters].sort((a, b) => {
    const w = (x) => (x.gender === "Woman" ? -1 : 0) +
                    (x.preferred_orientation === "landscape" ? -1 : 0);
    return w(a) - w(b);
  }).slice(0, 8);
  return (
    <div data-testid="presenter-picker">
      <button onClick={() => onSelect("")}
              className={`w-full text-left px-3 py-2 rounded-lg border-2 mb-2 ${
                !selected ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}
              data-testid="btn-no-presenter">
        <div className="font-bold text-sm">No presenter</div>
        <div className="text-[11px] text-slate-500">
          Product + voiceover only — fastest render.
        </div>
      </button>
      <div className="grid grid-cols-4 gap-2">
        {sorted.map(p => (
          <button key={p.id} onClick={() => onSelect(p.id)}
                  data-testid={`btn-presenter-${p.id}`}
                  title={`${p.name} · ${p.gender}`}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${
                    selected === p.id
                      ? "border-amber-400 ring-2 ring-amber-300"
                      : "border-slate-200 hover:border-slate-400"}`}>
            {p.preview_image_url
              ? <img src={p.preview_image_url} alt={p.name}
                     className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-slate-100 grid place-items-center text-[10px] text-slate-400">
                  {p.name}
                </div>}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1 text-white text-[10px] font-semibold">
              {p.name}
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <p className="text-[11px] text-slate-500 mt-2">
          Adds a HeyGen lip-synced presenter (~$0.25 per render, +60-90s).
        </p>
      )}
    </div>
  );
}
